import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { theme, useResponsive } from '@sweetgreen/garnish';

import {
  AccountPaymentCreditLoading,
  AccountScreenBorderedSection,
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenSectionTitle,
  AccountScreenTitle,
  CreditSection,
  NotFoundView,
  PaymentAccountSection,
} from '@order/components';
import {
  useCustomerCredit,
  usePaymentMethods,
  usePromoCode,
} from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import type { PaymentCreditScreenProps } from '../../navigation/AppNavigation.props';

export const PaymentCreditScreen = (props: PaymentCreditScreenProps) => {
  const { navigation } = props;

  const shouldSeparatePromoAndGiftCard = useFeatureFlag(
    'CELS-1410-separate-promo-and-gift-card',
  );

  // ─── Context ─────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { currentBreakpoint, match } = useResponsive();
  const { availableCredit, credits, isLoadingCredits, refetchCredits } =
    useCustomerCredit();

  // ─── Payment Methods ─────────────────────────────────────────────────

  const { paymentMethods, isLoadingPaymentMethods } = usePaymentMethods();

  // ─── Promo Code ──────────────────────────────────────────────────────

  const {
    promoCode,
    promoCodeInvalid,
    isSubmittingPromoCode,
    isPromoCodeApplied,
    setPromoCode,
    submitPromoCode,
  } = usePromoCode(refetchCredits);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const handlePressAvailableCredit = useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'CreditDetails',
    });
  }, [navigation]);

  const handlePressAddPromoCode = useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'CreditAdd',
    });
  }, [navigation]);

  // ─────────────────────────────────────────────────────────────────────

  if (shouldSeparatePromoAndGiftCard)
    return <NotFoundView withoutFooter={currentBreakpoint.isXS} />;

  if (isLoadingPaymentMethods) return <AccountPaymentCreditLoading />;

  return (
    <AccountScreenContainer screen="payment-credit">
      <AccountScreenTitle
        visibility="COMPACT"
        title={t('account.payment-promo.title')}
      />

      {/* PAYMENT */}
      <View style={match([styles.paymentSectionContainerXS, undefined])}>
        <PaymentAccountSection paymentMethods={paymentMethods} />
      </View>

      {/* CREDIT */}
      <AccountScreenSectionTitle title={t('account.credit-promo.title')} />
      <AccountScreenSection>
        <AccountScreenBorderedSection
          style={currentBreakpoint.isXS ? {} : styles.creditSpacing}
        >
          <CreditSection
            creditAmount={availableCredit}
            creditDetails={credits}
            promoCode={promoCode}
            isLoadingCredits={isLoadingCredits}
            isSubmittingPromoCode={isSubmittingPromoCode}
            hasPromoCodeError={promoCodeInvalid}
            isPromoCodeApplied={isPromoCodeApplied}
            onPressAvailableCredit={handlePressAvailableCredit}
            onPressAddPromoCode={handlePressAddPromoCode}
            setPromoCode={setPromoCode}
            onPressApplyPromoCode={submitPromoCode}
          />
        </AccountScreenBorderedSection>
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  creditSpacing: {
    paddingVertical: theme.spacing['6'],
  },
  paymentSectionContainerXS: {
    marginBottom: theme.spacing['10'],
  },
});
